import React from 'react';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import ContentSection from '../../../ContentSection';
import MessageButtonCard from '~shared/components/react/MessageButtonCard';
import { NO_RESULTS_ICON } from '~spa/Pages/ErrorPages/NoMarkets/constants';
import { MESSAGE_BUTTON_BLOCK_MESSAGE_LINK } from '~shared/components/react/MessageButtonCard/constants';
import { Link } from '~spa/Components/Link/Link';
import { getSportIdBySlug, getHasBoostedOddsSelections } from '~shared/utils/sportsbook';
import { seoUrlCompetitionSport, seoUrlTournamentOutrights } from '~spa/Utils/SeoUrls';
import i18n from '~shared/utils/i18n';
import {
    createBetCard,
    createThreeMarketBetCard,
    getMarketHeader,
    createDescriptionTemplate,
    showWidget,
} from '../../../../../utils/widgets';
import {
    THREE_MARKET_COLUMN_HEADERS,
} from '~shared/components/react/SportsWidget/constants.js';
import {
    convertDateToString,
    getTodayTomorrowDateLabel,
} from '~shared/utils/date-time';
import {
    FIRST_ELEMENT_KEY,
    I18N_KEY_NO_UPCOMING_MATCHES_48_HOURS,
    I18N_KEY_NO_UPCOMING_MATCHES_WEEK,
    I18N_KEY_NO_UPCOMING_MATCHES_WEEKS,
    I18N_KEY_NO_UPCOMING_MATCHES_YEAR,
} from '../../constants';
import WidgetContainer from '../../../../../ui/WidgetContainer';
import { GTM_UPCOMING_MATCHES, GTM_COMPETITIONS_PAGE } from '../../../../../utils/constants';
export default class UpcomingMatchesLayout extends React.Component {
    constructor() {
        super();

        this.getContentSectionParams = this.getContentSectionParams.bind(this);
        this.getContentSection = this.getContentSection.bind(this);
        this.createContentSection = this.createContentSection.bind(this);
    }

    static propTypes = {
        sport: PropTypes.string,
        widgetHeader: PropTypes.string,
        events: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array,
        ]),
        commonDataEvents: PropTypes.object,
        source: PropTypes.string,
        tournament: PropTypes.string,
        matchRequestLimit: PropTypes.string,
        localizedSport: PropTypes.string,
        category: PropTypes.string,
        tournamentId: PropTypes.string,
        isCompetition: PropTypes.bool,
    };

    /**
     * Get the parameters for the Content Section component
     * @param {String} dateLabel 'Today' or 'Tomorrow'
     * @param {Array} defaultMarkets Array of default markets
     * @param {Boolean} usesThreeMarketView Display markets using three column view or not
     * @return {Object} Content Section Params: headerText, isCollapsible and titleModifiers.
     */
    getContentSectionParams(dateLabel, defaultMarkets, usesThreeMarketView = false) {

        return usesThreeMarketView
            ? {
                headerText: getMarketHeader(this.props.sport, dateLabel, defaultMarkets, usesThreeMarketView),
                isCollapsible: false,
                titleModifiers: ['column-headers', THREE_MARKET_COLUMN_HEADERS],
            }
            : {
                headerText: getMarketHeader(this.props.sport, dateLabel, defaultMarkets),
                isCollapsible: false,
                titleModifiers: ['column-headers', 'label-headers'],
            };
    }

    /**
     * Get the Content Section component grouped by date
     * @param {Number} key For ContentSection key property
     * @param {String} dateLabel 'Today' or 'Tomorrow'
     * @param {Object} events List of events
     * @param {Object} commonEvents List of events in Common Data Store
     * @param {Object} defaultMarkets List of default markets
     * @param {Int} defaultMarketId Default market id
     * @param {Array} groupedMarketIds Grouped Market Ids for 3 column layout
     * @return {ContentSection} The Content Section element
     */
    getContentSection(key, dateLabel, events, commonEvents, defaultMarkets, defaultMarketId, groupedMarketIds) {
        const gtmSuffix = this.props.isCompetition ? GTM_COMPETITIONS_PAGE : GTM_UPCOMING_MATCHES;
        const gtmAttribute = `${this.props.source};${gtmSuffix};`;

        const [{event_id: eventId }] = events;
        const { usesThreeMarketView } = commonEvents[eventId];
        const contentSectionParams = this.getContentSectionParams(dateLabel, defaultMarkets, usesThreeMarketView);

        return (
            <ContentSection key={key} params={contentSectionParams}>
                {events.map((event, key) => {
                    const eventObj = commonEvents[event.event_id];
                    const defaultMarket = eventObj.markets[defaultMarketId];
                    const hasBoostedOddsSelections = Boolean(defaultMarket) && getHasBoostedOddsSelections(defaultMarket.selections);
                    const descriptionTemplate = createDescriptionTemplate(eventObj, false, gtmAttribute, hasBoostedOddsSelections);

                    return usesThreeMarketView
                        ? createThreeMarketBetCard(
                            eventObj,
                            groupedMarketIds,
                            key,
                            descriptionTemplate,
                            gtmAttribute,
                        )
                        : createBetCard(
                            eventObj,
                            defaultMarketId,
                            key,
                            descriptionTemplate,
                            gtmAttribute,
                        );
                })}
            </ContentSection>
        );
    }

    /**
     * Create a ContentSection element containing the list of events in the specified group
     * @param {Object} events The object with events data
     * @return {ContentSection} The ContentSection element
     */
    createContentSection(events) {
        // get events/markets ids and isolate the first of each
        let firstEvent = events[FIRST_ELEMENT_KEY];
        // common events info and sport slug
        const {
            commonDataEvents: commonEvents,
        } = this.props;
        // match state event ids with commonData event ids
        const commonEventsKeys = Object.keys(commonEvents);
        // widget events
        let widgetEvents = [];
        events.forEach((event) => {
            if (commonEventsKeys.includes(String(event.event_id))) {
                // events/sections info
                firstEvent = event;
                const commonEventInfo = commonEvents[event.event_id];
                const {
                    tournament_name: tournamentName,
                    category_name: categoryName,
                    tournament_slug: tournamentSlug,
                    tournament_logos: tournamentLogos,
                    sport_slug: sportSlug,
                    category_slug: categorySlug,
                    start_time : startTime,
                } = commonEventInfo;

                // TOURNAMENT SECTION
                const tournamentLabel = `${tournamentName}, ${categoryName}`;

                // check if create new tournament section
                const tournamentExists = widgetEvents.find(({ tournament_slug }) => tournament_slug === tournamentSlug);
                if (!tournamentExists) {
                    widgetEvents.push({
                        label: tournamentLabel,
                        tournament_slug: tournamentSlug,
                        dateEvents: [],
                        tournamentLogos,
                        sportSlug,
                        categorySlug,
                    });
                }
                const tournament = widgetEvents.find(({ tournament_slug }) => tournament_slug === tournamentSlug);

                // DATE SECTION
                const dateLabel = getTodayTomorrowDateLabel(startTime);

                // check if create new date section
                const searchForDate = typeof dateLabel === 'string' ? dateLabel : dateLabel.dateTime.toString();
                const dateExists = tournament.dateEvents.find(({ label }) => {

                    return typeof label === 'string'
                        ? label === searchForDate
                        : convertDateToString(new Date(label.dateTime)) === convertDateToString(new Date(searchForDate));
                });

                if (!dateExists) {
                    tournament.dateEvents.push({
                        label: dateLabel,
                        events: [],
                    });
                }
                const dateSection = tournament.dateEvents.find(({ label }) => {

                    return typeof label === 'string'
                        ? label === searchForDate
                        : convertDateToString(new Date(label.dateTime)) === convertDateToString(new Date(searchForDate));
                });

                // add event on the proper section
                dateSection.events.push(event);
            }
        });

        // get markets ids
        const { market_id: defaultMarketId, grouped_market_ids: groupedMarketIds } = firstEvent;

        // get default markets for the selection columns
        const defaultMarkets = commonEvents[firstEvent.event_id].markets[defaultMarketId];

        return (
            <div>
                {widgetEvents.map((tournamentSection, key) => {
                    const {
                        label,
                        tournament_slug,
                        dateEvents,
                        tournamentLogos,
                        sportSlug,
                        categorySlug,
                    } = tournamentSection;

                    return (
                        <ContentSection key={key} params={{
                            headerText: label,
                            isCollapsible: true,
                            titleModifiers: ['grouping-headers', 'default', 'betcard-padding'],
                            tournamentLogos,
                            sportSlug,
                            categorySlug,
                            tournamentSlug: tournament_slug,
                        }}>
                            {dateEvents.map((dateSection, key) => {

                                return this.getContentSection(
                                    key,
                                    dateSection.label,
                                    dateSection.events,
                                    commonEvents,
                                    defaultMarkets,
                                    defaultMarketId,
                                    groupedMarketIds,
                                );
                            })}
                        </ContentSection>
                    );
                })}
            </div>
        );
    }

    /**
     * Returns a url linking to competitions or outrights tab depending on what is on screen.
     * @property {*} object with required information to generate url
     * @returns {string} url for competitions tab or outrights tab depending on information.
     */
    getLinkURL({tournament, sport, localizedSport, category, tournamentId}) {
        const segments = [
            (!tournament) ? sport : localizedSport,
            category,
            tournament,
        ]
        .filter((slug) => Boolean(slug))
        .join('/');
        const baseSegments = `/${segments}`;

        return tournament ? seoUrlTournamentOutrights(`${baseSegments}/${i18n.t('url.outrights')}`, tournamentId)
                          : seoUrlCompetitionSport(`${sport}/${i18n.t('url.competitions')}`, getSportIdBySlug(sport));
    }

    /**
     * Takes a number of days and returns the message that should be displayed to the user when
     * there are no matches within the next <numberOfDays> days.
     * @param {string|number} numberOfDays number of days is used to choose which translation to use.
     * @return {string | string[]} array containing message and the link to the competitions tab.
     */
    getLocalizedMessage(numberOfDays = 2) {
        let days = numberOfDays;
        if(typeof days === 'string'){
            days = parseInt(days.match(/\d+/));
        }

        const linkURL = this.getLinkURL(this.props);
        const week = 7, year = 365;

        // Takes the first thing in angle brackets that it finds in given string and adds the link to it.
        const addCompetitionsLink = (message) => {
            const regex = /<\d>(.+)<\/\d>/;
            const localizedCompetitionsMatch = message.match(regex);
            if (!localizedCompetitionsMatch){ return message; }
            const localizedCompetitions = localizedCompetitionsMatch.pop();

            return message.split(regex).map((str, key) => {
                if(str === localizedCompetitions) {
                    return <Link key={key} to={linkURL} className={MESSAGE_BUTTON_BLOCK_MESSAGE_LINK}>{str}</Link>;
                }

                return <React.Fragment key={key}>{str}</React.Fragment>;
            });
        };

        // If number of days is less than a week always return original '48 hours' message.
        if (!days || days < week) {
            return addCompetitionsLink(i18n.t(I18N_KEY_NO_UPCOMING_MATCHES_48_HOURS));
        }

        const isWeeks = days < year;
        if(isWeeks) {
            const weeks = Math.floor(days/week);
            const isSingular = weeks === 1;
            // select message based on if weeks should be plural or singular.
            const message = i18n
            .t(isSingular ? I18N_KEY_NO_UPCOMING_MATCHES_WEEK : I18N_KEY_NO_UPCOMING_MATCHES_WEEKS)
            .replace(/<\d>XX<\/\d>/, weeks);

            return addCompetitionsLink(message);
        }

        // year
        return addCompetitionsLink(i18n.t(I18N_KEY_NO_UPCOMING_MATCHES_YEAR));
    }

    /**
     * Render the component
     * @returns {JSX.Element} Returns a React Element
     */
    render() {
        const { events, commonDataEvents, widgetHeader, sport, matchRequestLimit } = this.props;
        const noMatchesMessage = this.getLocalizedMessage(matchRequestLimit);

        const noMatchesContent = isArray(events) && !events.length
            ? <MessageButtonCard
                MessageIconClassName={NO_RESULTS_ICON}
                message={noMatchesMessage} />
            : <></>;

        const content = showWidget(events, commonDataEvents)
            ? this.createContentSection(events)
            : noMatchesContent;

        return(
            <div>
                <WidgetContainer
                    title={widgetHeader}
                    titleIconClassName={sport}>
                    {content}
                </WidgetContainer>
            </div>
        );
    }
}
